import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Column,
  useTable,
  useGlobalFilter,
  useFilters,
  TableInstance,
  HeaderGroup,
  ColumnInstance,
  UseSortByColumnProps,
  useSortBy,
} from 'react-table';
import './../../recommendations/Table-widgets/RecommendationTableWidget.scss';
import { DefaultColumnFilter } from '../../../components/data-tables/helpers/DefaultColumnFilter';
import { createMultiConditionFilter } from '../../../utilities/tableFilters';
import Helpers, { formatNumber } from '../../../utilities/Helpers';
import useConfig from '../../../hooks/useConfig';
import { DetailedDailyDowntimeItem } from './DetailedDailyTypes';
import Tooltip from '../../common/tooltip/ToolTip';

interface DataTableProps {
  data: DetailedDailyDowntimeItem[];
  columns: Column<DetailedDailyDowntimeItem>[];
  searchTerm?: string;
}

const DataTable: React.FC<DataTableProps> = ({ data = [], columns, searchTerm = '' }) => {
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const currentFiltersRef = useRef(currentFilters);
  const { enablePumpChecker } = useConfig();

  // Calculate totals based on widget header
  const totals = useMemo(() => {
    const totalsData: { [key: string]: string | number } = {};

    const LostOil = data.reduce((sum: number, item: any) => sum + (Number(item.LostOil) || 0), 0);
    const Hours = data.reduce((sum: number, item: any) => {
      const currHr = enablePumpChecker ? Number(item.Hours) : Number(item.hours) || 0;
      return sum + currHr;
    }, 0);
    totalsData['LostOil'] = LostOil;
    totalsData['Hours'] = Hours;

    return totalsData;
  }, [data, enablePumpChecker]);

  const showTotal = useMemo(() => {
    return (
      Object.keys(totals).length > 0 && columns.some((col) => col.accessor === 'LostOil' || col.accessor === 'Hours')
    );
  }, [columns, totals]);

  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  const multiConditionFilter = useMemo(() => createMultiConditionFilter(setCurrentFilters, currentFiltersRef), []);

  const defaultColumn: any = useMemo(
    () => ({
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
        />
      ),
    }),
    [],
  );

  //handle leaf column filters
  const assignFilterToLeafColumns = (cols: any[], filterFn: any): any[] => {
    return cols.map((col) => {
      if (col.columns && col.columns.length > 0) {
        return {
          ...col,
          columns: assignFilterToLeafColumns(col.columns, filterFn),
        };
      } else {
        return {
          ...col,
          filter: col.filter || filterFn,
        };
      }
    });
  };

  const tableColumns = useMemo(() => {
    return assignFilterToLeafColumns(columns, multiConditionFilter);
  }, [columns, multiConditionFilter]);

  const tableInstance = useTable<DetailedDailyDowntimeItem>(
    {
      columns: tableColumns,
      data: data,
      defaultColumn,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setGlobalFilter, setAllFilters } =
    tableInstance as TableInstance<DetailedDailyDowntimeItem> & {
      setGlobalFilter: (filterValue: string | undefined) => void;
      setAllFilters: (filters: any[]) => void;
    };

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    setCurrentFilters(remainingFilters);

    if (remainingFilters.length === 0) {
      setAllFilters([]);
    } else {
      setAllFilters(
        remainingFilters.map((filter) => ({
          id: filter.columnId,
          value: filter.filterValue,
        })),
      );
    }
  };

  useEffect(() => {
    setGlobalFilter(searchTerm);
  }, [searchTerm, setGlobalFilter]);

  return (
    <section className='table-container-wrapper'>
      <div className='table-container recommendations-table-container'>
        <table {...getTableProps()} className='data-table'>
          <thead>
            {headerGroups.map((headerGroup: HeaderGroup<DetailedDailyDowntimeItem>, groupIdx: number) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className={groupIdx === 0 ? 'recommendations-grouped-header-row' : ''}
              >
                {headerGroup.headers.map((col) => {
                  const column = col as unknown as ColumnInstance<DetailedDailyDowntimeItem> &
                    UseSortByColumnProps<DetailedDailyDowntimeItem> as any;
                  return (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className={`h-[44px] pt-[16px] pb-[16px] pl-[12px] pr-[12px] border-t border-[#4a5463] text-left ${column.id}-column`}
                      style={{
                        ...(column.columns && column.columns.length > 0
                          ? {
                              borderLeft: '2px solid #5d6675',
                              borderRight: '2px solid #5d6675',
                            }
                          : {}),
                      }}
                    >
                      <div
                        className='flex gap-[4px] items-center table-header'
                        style={{
                          ...(column.columns && column.columns.length > 0
                            ? {
                                justifyContent: 'center',
                                width: '100%',
                              }
                            : {}),
                        }}
                      >
                        <span className='text-[12px] font-semibold text-[#f7f9f9]'>
                          {typeof column.render('Header') === 'string'
                            ? Helpers.toSentenceCase(column.render('Header'))
                            : column.render('Header')}
                        </span>
                        {column.isSorted && (
                          <span
                            className={`freq-sort-icon ${
                              column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : 'default'
                            }`}
                            style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                          />
                        )}
                        {column.canFilter && !column.columns ? <span>{column.render('Filter')}</span> : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={`${row.original.uniqueId}`}>
                  {row.cells.map((cell) => {
                    if (cell.column.id === 'Comment') {
                      return (
                        <td {...cell.getCellProps()} className='text-left p-3' style={{ textAlign: 'left' }}>
                          <Tooltip content={row.original.Comment} direction='left'>
                            <div className={`${row.cells.length > 1 ? 'max-w-[700px]' : 'max-w-[90w]'} truncate`}>
                              {cell.render('Cell')}
                            </div>
                          </Tooltip>
                        </td>
                      );
                    } else {
                      return (
                        <td {...cell.getCellProps()} className='text-left p-3' style={{ textAlign: 'left' }}>
                          {cell.render('Cell')}
                        </td>
                      );
                    }
                  })}
                </tr>
              );
            })}
            {/* Totals row */}
            {rows.length > 0 && showTotal && (
              <tr className='sticky bottom-0'>
                {headerGroups[headerGroups.length - 1].headers.map((col) => {
                  const column = col as any;
                  let cellContent = '';

                  // Check if this column should show a total
                  if (totals[column.id] || totals[column.accessor]) {
                    const totalValue = totals[column.id] || totals[column.accessor];
                    const roundedTotal = formatNumber(totalValue, 3);
                    cellContent = `Total: ${roundedTotal}`;
                  }

                  return (
                    <td key={column.id} className='bg-[#131f2e]'>
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className='no-matching-records-container text-center p-4 pl-6 pr-6 h-[250px] flex items-center justify-center'>
            <span className='no-matching-records-message text-[#f7f9f9]'>No records available</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default DataTable;
